import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Page ka title / description / canonical / OG / Twitter — ek jagah se.
 *
 * Pehle do dikkat thi:
 *  1. canonical = baseUrl + location.pathname. Kuch pages baseUrl me pehle hi
 *     path bhej rahe the (product page: `${site}/product/${id}`), to canonical
 *     `https://chawlaispat.com/product/<id>/product/<id>/<name>` ban jaata tha —
 *     aisa URL exist hi nahi karta. Ab baseUrl se sirf origin liya jaata hai.
 *  2. cleanup unmount par <meta name="description"> ko DOM se hata deta tha, to
 *     route badalne par kuch der description hoti hi nahi thi. Ab kuch remove
 *     nahi hota — agla page apni value likh deta hai.
 *
 * CRA client-side render hai: Google JS chala kar ye tags padh leta hai, par
 * WhatsApp/Facebook preview sirf server HTML dekhte hain. Prerender ka ticket
 * alag hai (SEO plan Phase 2).
 */

export type PageMetaOptions = {
  /** Poora canonical URL — na do to origin + current path. */
  canonical?: string;
  /** og:image ka absolute URL. */
  image?: string;
  /** og:type — product page par "product", baaki "website". */
  ogType?: string;
  /** true = <meta name="robots" content="noindex, follow"> */
  noindex?: boolean;
  /** true = kuch mat likho. Jab parent component pehle hi meta set kar chuka ho. */
  skip?: boolean;
};

const upsertMeta = (attr: "name" | "property", key: string, content: string) => {
  const sel = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(sel) as HTMLMetaElement | null;
  if (!content) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertLink = (rel: string, href: string) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

/** "https://chawlaispat.com/product/123" -> "https://chawlaispat.com" */
const originOf = (u: string): string => {
  const m = String(u || "").match(/^https?:\/\/[^/]+/);
  return m ? m[0] : String(u || "").replace(/\/+$/, "");
};

const usePageMeta = (
  title: string,
  description: string,
  baseUrl: string,
  options: PageMetaOptions = {}
) => {
  const location = useLocation();
  const { canonical, image, ogType, noindex, skip } = options;

  useEffect(() => {
    if (skip) return;
    const origin = originOf(baseUrl);
    // query string canonical me nahi jaati — ?page=2 wale duplicate na banein.
    const path = location.pathname === "/" ? "/" : location.pathname.replace(/\/+$/, "");
    const url = canonical || `${origin}${path === "/" ? "" : path}` || origin;
    // default = 1200x630 wala card; product page apni image bhejta hai.
    const img = image || `${origin}/og-image.png`;

    document.title = title;
    upsertMeta("name", "description", description);
    upsertLink("canonical", url);

    upsertMeta("name", "robots", noindex ? "noindex, follow" : "index, follow, max-image-preview:large");

    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:type", ogType || "website");
    upsertMeta("property", "og:image", img);
    // width/height sirf default card ke liye sahi hain — custom image par hata dete hain
    upsertMeta("property", "og:image:width", image ? "" : "1200");
    upsertMeta("property", "og:image:height", image ? "" : "630");
    upsertMeta("property", "og:image:alt", title);
    upsertMeta("property", "og:site_name", "Chawla Ispat");
    upsertMeta("property", "og:locale", "en_IN");

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", img);
  }, [title, description, baseUrl, location.pathname, canonical, image, ogType, noindex, skip]);
};

export default usePageMeta;
